import React, { useState, useRef, useEffect } from 'react';
import styles from './CorrectionButton.module.css';
import { CorrectionForm } from './CorrectionForm';
import type { CorrectionFormData } from './CorrectionForm';

export interface CorrectionButtonProps {
  messageId: string;
  messageContent: string;
  skill?: string;
  visible?: boolean;
  onSubmit: (messageId: string, data: CorrectionFormData) => void;
}

export const CorrectionButton: React.FC<CorrectionButtonProps> = ({
  messageId,
  messageContent,
  skill,
  visible = false,
  onSubmit,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSubmit = (data: CorrectionFormData) => {
    onSubmit(messageId, data);
    setIsOpen(false);
  };

  return (
    <div className={styles.wrapper}>
      <button
        className={`${styles.button} ${visible || isOpen ? styles.visible : ''}`.trim()}
        onClick={() => setIsOpen((prev) => !prev)}
        type="button"
        aria-label="Correct this response"
      >
        <span className={styles.icon}>&#9998;</span>
        Correct this
      </button>

      {isOpen && (
        <>
          <div
            className={styles.overlay}
            onClick={() => setIsOpen(false)}
          />
          <div className={styles.formContainer} ref={formRef}>
            <div className={styles.formHeader}>
              <span className={styles.formTitle}>
                <span className={styles.formTitleIcon}>&#9998;</span>
                Submit Correction
              </span>
              <button
                type="button"
                className={styles.closeButton}
                onClick={() => setIsOpen(false)}
                aria-label="Close correction form"
              >
                &#10005;
              </button>
            </div>
            <CorrectionForm
              originalValue={messageContent}
              messageId={messageId}
              skill={skill}
              onSubmit={handleSubmit}
              onCancel={() => setIsOpen(false)}
            />
          </div>
        </>
      )}
    </div>
  );
};
